/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, useState as defaultUseState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import styled from 'styled-components';

import { Portal } from 'react-portal';

import type { VirtualElement } from '@popperjs/core';
import { usePopper as defaultUsePopper } from 'react-popper';
import type { Modifier } from 'react-popper';

import { Content } from './Content';
import type { DropdownBase, DropdownRenderProps } from './types';

export const StyledPopper = styled.div({
  zIndex: 1100,
});

export type DropdownProps = DropdownBase & {
  /**
   * Функция рендера элемента, открывающего выпадающего меню
   * @param {Object} renderProps
   * @param {boolean} renderProps.isDropdownOpen - открыто ли меню
   * @param {Function} renderProps.onClick - обработчик нажатия на элемент, открывающий/закрывающий меню
   * @param {React ref} renderProps.ref - ref элемента, относительно которого позиционируется выпадающее меню
   */
  children: (renderProps: DropdownRenderProps) => ReactNode;

  /**
   * Служебное
   */
  useState?: typeof defaultUseState;
  /**
   * Служебное
   */
  usePopper?: typeof defaultUsePopper;
  /**
   * Точка монтирования портала (HTMLElement | selector)
   */
  target?: HTMLElement | string;
  /**
   * Дополнительная обработка при закрытии дропдауна
   */
  onDropdownClose?: () => void;
};

export function Dropdown({
  hasHorizontalOffsets = true,
  hasVerticalOffsets = true,

  renderContent,

  placement = 'bottom-start',
  popperStyle = undefined,
  popperProps = undefined,
  modifiers = undefined,

  useState = defaultUseState,
  usePopper = defaultUsePopper,

  children,
  target = undefined,
  onDropdownClose = undefined,
}: DropdownProps): ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [referenceEl, setReferenceEl] = useState<
  Element | VirtualElement | null
  >(null);
  const [popperEl, setPopperEl] = useState<HTMLElement | null>(null);

  const mergedModifiers = useMemo<Modifier<any>[]>(() => {
    if (hasVerticalOffsets || hasHorizontalOffsets) {
      return [
        {
          name: 'offset',

          options: {
            offset: ({ placement: resPlacement }): [number, number] => {
              switch (resPlacement) {
                case 'right-start':
                case 'right':
                case 'right-end':
                case 'left-start':
                case 'left':
                case 'left-end':
                  return [0, hasHorizontalOffsets ? 4 : 0];

                case 'bottom-start':
                case 'bottom':
                case 'bottom-end':
                case 'top-start':
                case 'top':
                case 'top-end':
                  return [0, hasVerticalOffsets ? 4 : 0];

                default:
                  return [0, 0];
              }
            },
          },
        },

        ...(modifiers || []),
      ];
    }

    return modifiers || [];
  }, [modifiers, hasVerticalOffsets, hasHorizontalOffsets]);

  const { styles, attributes } = usePopper(referenceEl, popperEl, {
    ...popperProps,
    placement,
    modifiers: mergedModifiers,
  });

  const open = useCallback((): void => {
    setIsOpen(true);
  }, []);

  const close = useCallback((): void => {
    setIsOpen(false);
    onDropdownClose?.();
  }, [onDropdownClose]);

  const onClickOutside = useCallback(
    (event: Event): void => {
      if (
        !referenceEl
        || (referenceEl as Element).contains(event.target as Node)
      ) {
        return;
      }

      close();
    },
    [referenceEl, close],
  );

  return (
    <>
      {children({
        isDropdownOpen: isOpen,
        onClick: isOpen ? close : open,
        ref: setReferenceEl,
      })}

      {isOpen && (
        <Portal
          node={
            (typeof target === 'string'
              ? document && document.querySelector(target)
              : target) || undefined
          }
        >
          <StyledPopper
            style={{
              ...popperStyle,
              ...styles.popper,
            }}
            {...attributes.popper}
            ref={setPopperEl}
          >
            <Content onClickOutside={onClickOutside}>
              {renderContent({
                close,
              })}
            </Content>
          </StyledPopper>
        </Portal>
      )}
    </>
  );
}
